import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiSave, FiX, FiUpload } from 'react-icons/fi';
import { DateTime } from 'luxon';
import { getChicagoNow, createChicagoDateTime } from '../../utils/datetime-comprehensive';
import apiService from '../../services/api';
import RichTextEditorTipTap from '../common/RichTextEditor';
import logger from '../../utils/logger';
import styles from './CreateAssignmentInline.module.css';

function CreateAssignmentInline({ onAssignmentCreated, onBack }) {
  const [formData, setFormData] = useState({
    courseId: '',
    title: '',
    description: { content_json: null, content_html: '', content_plain: '' },
    points: 10,
    dueDate: '',
    startDate: '',
    allowResubmission: false,
    allowWeeklySubmissions: false,
    maxWeeklySubmissions: ''
  });
  const [courses, setCourses] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');

  useEffect(() => {
    fetchInstructorCourses();
  }, []);

  const fetchInstructorCourses = async () => {
    try {
      const response = await apiService.getCourses();
      if (response.success && response.courses) {
        const currentUser = apiService.getCurrentUser();
        // Admins can create assignments for any active course; instructors are limited to their own active courses.
        const availableCourses = response.courses.filter(course => {
          if (!course.is_active) return false;
          return currentUser?.role === 'admin' || course.instructor_id === currentUser?.id;
        });
        setCourses(availableCourses);

        // Auto-select if there's exactly one active course
        if (availableCourses.length === 1) {
          setFormData(prev => ({ ...prev, courseId: String(availableCourses[0].id) }));
        }
      }
    } catch (error) {
      logger.error('Error fetching courses', { error });
      setCourses([]);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.courseId) {
      newErrors.courseId = 'Please select a course';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Assignment title is required';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title must be 255 characters or less';
    }

    // Validate instructions
    let hasInstructions = false;
    if (formData.description) {
      if (typeof formData.description === 'object') {
        // Check for TipTap editor format (content_plain and content_html)
        if (formData.description.content_plain !== undefined) {
          const hasPlainText = formData.description.content_plain && formData.description.content_plain.trim() !== '';
          const hasHtml = formData.description.content_html && formData.description.content_html.trim() !== '' && formData.description.content_html !== '<p></p>';
          hasInstructions = hasPlainText || hasHtml;
        } else {
          // Check legacy format (text and html)
          const hasText = formData.description.text && formData.description.text.trim() !== '';
          const hasHtml = formData.description.html && formData.description.html.trim() !== '' && formData.description.html !== '<p></p>';
          hasInstructions = hasText || hasHtml;
        }
      } else if (typeof formData.description === 'string') {
        hasInstructions = formData.description.trim() !== '';
      }
    }
    
    if (!hasInstructions) {
      newErrors.description = 'Instructions are required';
    }

    if (formData.dueDate) {
      const dueDate = DateTime.fromISO(formData.dueDate).setZone('America/Chicago');
      const now = getChicagoNow();
      if (dueDate <= now) {
        newErrors.dueDate = 'Due date must be in the future';
      }
    }

    if (formData.points < 0 || formData.points > 1000) {
      newErrors.points = 'Points must be between 0 and 1000';
    }

    // Validate weekly submissions
    if (formData.allowWeeklySubmissions) {
      if (!formData.maxWeeklySubmissions || formData.maxWeeklySubmissions < 1) {
        newErrors.maxWeeklySubmissions = 'Maximum weekly submissions is required when weekly submissions are enabled';
      } else if (formData.maxWeeklySubmissions > 52) {
        newErrors.maxWeeklySubmissions = 'Maximum weekly submissions cannot exceed 52';
      }
      if (!formData.startDate) {
        newErrors.startDate = 'Start date & time is required when weekly submissions are enabled';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleFileChange = async (file) => {
    if (file) {
      if (file.type !== 'application/pdf') {
        setErrors(prev => ({ ...prev, pdfFile: 'Please select a PDF file' }));
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setErrors(prev => ({ ...prev, pdfFile: 'File size must be less than 10MB' }));
        return;
      }

      try {
        const base64 = await convertFileToBase64(file);
        setPdfFile(file);
        setPdfBase64(base64);
        setErrors(prev => ({ ...prev, pdfFile: '' }));
      } catch (error) {
        setErrors(prev => ({ ...prev, pdfFile: 'Error processing file' }));
      }
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const removePdfFile = () => {
    setPdfFile(null);
    setPdfBase64('');
    setErrors(prev => ({ ...prev, pdfFile: '' }));
  };

  const setDateShortcut = (days) => {
    const now = getChicagoNow();
    const targetDate = now.plus({ days }).set({ hour: 19, minute: 0, second: 0, millisecond: 0 });
    
    // Format for datetime-local input (YYYY-MM-DDTHH:MM)
    const formattedDate = targetDate.toFormat('yyyy-MM-dd\'T\'HH:mm');
    handleInputChange('dueDate', formattedDate);
  };

  const clearDueDate = () => {
    handleInputChange('dueDate', '');
  };

  const setPointsShortcut = (points) => {
    handleInputChange('points', points);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      // Convert start date from datetime-local (Chicago time) to UTC
      let startDateUTC = null;
      if (formData.startDate) {
        const startDateParts = formData.startDate.split('T');
        const startDateChicago = createChicagoDateTime(startDateParts[0], startDateParts[1]);
        startDateUTC = startDateChicago.toUTC().toISO();
      }
      
      // Convert due date from datetime-local (Chicago time) to UTC
      let dueDateUTC = null;
      if (formData.dueDate) {
        // datetime-local input gives us YYYY-MM-DDTHH:MM format
        // We need to interpret this as Chicago time and convert to UTC
        const chicagoDateTime = createChicagoDateTime(
          formData.dueDate.split('T')[0], // date part
          formData.dueDate.split('T')[1]  // time part
        );
        
        if (chicagoDateTime) {
          dueDateUTC = chicagoDateTime.toUTC().toISO();
        }
      }

      let processedDescription = null;
      if (typeof formData.description === 'object' && formData.description?.html) {
        processedDescription = formData.description.html;
        logger.debug('Using html from description object', { descriptionLength: processedDescription.length });
      } else if (typeof formData.description === 'object' && formData.description?.content_html) {
        processedDescription = formData.description.content_html;
        logger.debug('Using content_html from description object', { descriptionLength: processedDescription.length });
      } else if (typeof formData.description === 'string') {
        processedDescription = formData.description.trim();
        logger.debug('Using string description', { descriptionLength: processedDescription.length });
      } else {
        logger.debug('Description is null/undefined');
      }

      // Build assignment data, only including optional fields if they have values
      // express-validator's .optional() only skips validation for undefined/missing fields, not null
      const assignmentData = {
        courseId: parseInt(formData.courseId),
        title: formData.title.trim(),
        description: processedDescription,
        points: parseInt(formData.points),
        type: 'other',
        allowResubmission: formData.allowResubmission,
        allowWeeklySubmissions: formData.allowWeeklySubmissions
      };

      // Only include optional fields if they have actual values
      if (dueDateUTC) {
        assignmentData.dueDate = dueDateUTC;
      }
      if (startDateUTC) {
        assignmentData.startDate = startDateUTC;
      }
      if (formData.allowWeeklySubmissions && formData.maxWeeklySubmissions) {
        assignmentData.maxWeeklySubmissions = parseInt(formData.maxWeeklySubmissions);
      }
      if (pdfBase64) {
        assignmentData.pdfAttachment = pdfBase64;
      }

      logger.debug('Final assignment data being sent', { 
        assignmentData: { ...assignmentData, description: assignmentData.description?.substring(0, 100) },
        descriptionLength: processedDescription ? processedDescription.length : 0 
      });

      const response = await apiService.createAssignment(assignmentData);

      if (response.success) {
        // Dispatch custom event for other components to listen to
        window.dispatchEvent(new CustomEvent('assignmentCreated', {
          detail: { assignment: response.assignment }
        }));
        
        // Call the callback after a short delay
        setTimeout(() => {
          onAssignmentCreated(response.assignment);
        }, 2000);
      } else {
        setErrors({ submit: response.error || 'Failed to create assignment' });
      }
    } catch (error) {
      logger.error('Error creating assignment', { error, errorData: error.data, assignmentData: { courseId: formData.courseId, title: formData.title } });
      // Show actual validation error if available
      let errorMessage = 'Failed to create assignment. Please try again.';
      if (error.data?.details) {
        // express-validator returns an array of validation errors
        errorMessage = error.data.details.map(d => d.msg || d.message).join(', ');
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.data?.error) {
        errorMessage = error.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const pointsChips = [0, 10, 20, 25, 50, 100];
  const dateShortcuts = [
    { label: 'Tomorrow', days: 1 },
    { label: '3 days', days: 3 },
    { label: '1 week', days: 7 },
    { label: '2 weeks', days: 14 },
    { label: 'No due date', days: null }
  ];

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentWrapper}>
        {/* Header */}
        <div className={styles.headerBar}>
          <div className={styles.headerLeft}>
            <button 
              className={styles.backButton} 
              onClick={onBack} 
              disabled={loading}
              type="button"
            >
              <FiArrowLeft size={16} />
              Back to Assignments
            </button>
            <h1 className={styles.pageTitle}>Create Assignment</h1>
            <p className={styles.pageSubtitle}>
              Set details, due date, instructions, and attachments.
            </p>
          </div>
        </div>

        {/* Form - Single Column Layout */}
        <form className={styles.form} onSubmit={handleSubmit}>
          {/* Course Selection */}
          <motion.div
            className={styles.mainFormCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Course <span className={styles.required}>*</span>
              </label>
              <select
                className={`${styles.select} ${errors.courseId ? styles.error : ''}`}
                value={formData.courseId}
                onChange={(e) => handleInputChange('courseId', e.target.value)}
              >
                <option value="">Select a course</option>
                {courses.map(course => (
                  <option key={`course-${course.id}`} value={course.id}>
                    {course.code} - {course.name}
                  </option>
                ))}
              </select>
              {errors.courseId && (
                <div className={styles.errorMessage}>{errors.courseId}</div>
              )}
            </div>
          </motion.div>

          {/* Assignment Title */}
          <motion.div
            className={styles.mainFormCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.05 }}
          >
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Assignment Title <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                className={styles.input}
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter assignment title"
                maxLength={255}
              />
              {errors.title && <div className={styles.errorMessage}>{errors.title}</div>}
            </div>
          </motion.div>

          {/* Instructions - Rich Text Editor */}
          <motion.div
            className={styles.mainFormCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div className={styles.formGroup}>
              <label className={styles.label}>
                Instructions <span className={styles.required}>*</span>
              </label>
              <div className={styles.richTextEditor}>
                <RichTextEditorTipTap
                  value={formData.description}
                  onChange={(value) => handleInputChange('description', value)}
                  placeholder=""
                  enableImages
                />
              </div>
              {errors.description && <div className={styles.errorMessage}>{errors.description}</div>}
            </div>
          </motion.div>

          {/* Settings Card - Full Width Below Editor */}
          <motion.div
            className={styles.settingsPanelCard}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            <h3 className={styles.sectionTitle}>Settings</h3>
            
            {/* Grading and Scheduling Groups - Side by Side */}
            <div className={styles.gradingSchedulingRow}>
              {/* Grading Group */}
              <div className={styles.settingsGroup}>
                <div className={styles.groupHeader}>
                  <h4 className={styles.groupTitle}>Grading</h4>
                </div>
                <div className={styles.groupBody}>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Points <span className={styles.required}>*</span>
                    </label>
                    <input
                      type="number"
                      className={styles.input}
                      min="0"
                      value={formData.points}
                      onChange={(e) => handleInputChange('points', parseInt(e.target.value) || 0)}
                      placeholder="10"
                    />
                    {errors.points && <div className={styles.errorMessage}>{errors.points}</div>}
                    <div className={styles.chipContainer}>
                      {pointsChips.map(points => (
                        <button 
                          key={points} 
                          type="button" 
                          className={styles.chip} 
                          onClick={() => setPointsShortcut(points)}
                        >
                          {points}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduling Group */}
              <div className={styles.settingsGroup}>
                <div className={styles.groupHeader}>
                  <h4 className={styles.groupTitle}>Scheduling</h4>
                </div>
                <div className={styles.groupBody}>
                  {formData.allowWeeklySubmissions ? (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Start Date & Time <span className={styles.required}>*</span>
                      </label>
                      <input
                        type="datetime-local"
                        className={styles.input}
                        value={formData.startDate}
                        onChange={(e) => handleInputChange('startDate', e.target.value)}
                        required={formData.allowWeeklySubmissions}
                      />
                      <div className={styles.helperText}>
                        Choose the week start date. Each submission is due when the week ends.
                      </div>
                      {errors.startDate && <div className={styles.errorMessage}>{errors.startDate}</div>}
                    </div>
                  ) : (
                    <div className={styles.formGroup}>
                      <label className={styles.label}>
                        Due Date & Time (Optional)
                      </label>
                      <input
                        type="datetime-local"
                        className={styles.input}
                        value={formData.dueDate}
                        onChange={(e) => handleInputChange('dueDate', e.target.value)}
                        placeholder="Leave empty for no due date"
                      />
                      <div className={styles.chipContainer}>
                        {dateShortcuts.map(shortcut => (
                          shortcut.days === null ? (
                            <button
                              key="no-due-date"
                              type="button"
                              className={styles.chip}
                              onClick={clearDueDate}
                            >
                              {shortcut.label}
                            </button>
                          ) : (
                            <button
                              key={shortcut.days}
                              type="button"
                              className={styles.chip}
                              onClick={() => setDateShortcut(shortcut.days)}
                            >
                              {shortcut.label}
                            </button>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Submissions Group */}
            <div className={styles.settingsGroup}>
              <div className={styles.groupHeader}>
                <h4 className={styles.groupTitle}>Submissions</h4>
              </div>
              <div className={styles.groupBody}>
                <div className={styles.togglesRow}>
                  <div className={`${styles.formGroup} ${styles.toggleFormGroup}`}>
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={formData.allowResubmission}
                          onChange={(e) => handleInputChange('allowResubmission', e.target.checked)}
                        />
                        <span />
                      </label>
                      <div>
                        <label className={styles.toggleLabel} htmlFor="allowResubmission">
                          Allow resubmission
                        </label>
                        <div className={styles.toggleHelper}>Students can submit multiple times</div>
                      </div>
                    </div>
                  </div>

                  <div className={`${styles.formGroup} ${styles.toggleFormGroup}`}>
                    <div className={styles.toggleGroup}>
                      <label className={styles.toggleSwitch}>
                        <input
                          type="checkbox"
                          checked={formData.allowWeeklySubmissions}
                          onChange={(e) => {
                            handleInputChange('allowWeeklySubmissions', e.target.checked);
                            if (!e.target.checked) {
                              handleInputChange('maxWeeklySubmissions', '');
                            }
                          }}
                        />
                        <span />
                      </label>
                      <div>
                        <label className={styles.toggleLabel} htmlFor="allowWeeklySubmissions">
                          Allow weekly submissions
                        </label>
                        <div className={styles.toggleHelper}>Students can submit one reflection per week</div>
                      </div>
                    </div>
                    {formData.allowWeeklySubmissions && (
                      <div className={styles.maxWeeklySubmissionsInput}>
                        <label className={styles.label}>
                          Maximum Weekly Submissions <span className={styles.required}>*</span>
                        </label>
                        <input
                          type="number"
                          className={styles.input}
                          value={formData.maxWeeklySubmissions}
                          onChange={(e) => handleInputChange('maxWeeklySubmissions', e.target.value)}
                          min="1"
                          max="52"
                          placeholder="e.g. 8"
                        />
                        <div className={styles.helperText}>
                          Students can submit one reflection per week, up to the maximum number set.
                        </div>
                        {errors.maxWeeklySubmissions && <div className={styles.errorMessage}>{errors.maxWeeklySubmissions}</div>}
                      </div>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <input
                    type="file"
                    className={styles.fileInput}
                    accept=".pdf"
                    onChange={handleFileInputChange}
                    id="pdf-upload"
                  />
                  <div
                    className={`${styles.fileUploadArea} ${errors.pdfFile ? styles.hasError : ''}`}
                    onClick={() => document.getElementById('pdf-upload').click()}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <div className={styles.fileUploadContent}>
                      <div className={styles.fileUploadIcon}>
                        <FiUpload />
                      </div>
                      <div className={styles.fileUploadText}>
                        {pdfFile ? 'Change PDF File' : 'Upload PDF Attachment (Optional) Max 10MB'}
                      </div>
                      <div className={styles.fileUploadSubtext}>
                        {pdfFile ? pdfFile.name : 'Click to upload or drag and drop'}
                      </div>
                    </div>
                  </div>
                  {pdfFile && (
                    <div className={styles.fileInfo}>
                      <div className={styles.fileName}>{pdfFile.name}</div>
                      {pdfFile.size > 0 && (
                        <div className={styles.fileSize}>{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</div>
                      )}
                      <button type="button" className={styles.removeFileButton} onClick={removePdfFile}>
                        <FiX size={18} />
                      </button>
                    </div>
                  )}
                  {errors.pdfFile && <div className={styles.errorMessage}>{errors.pdfFile}</div>}
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className={styles.errorMessage} style={{ marginTop: '1rem' }}>{errors.submit}</div>
            )}
          </motion.div>

          {/* Footer Actions */}
          <div className={styles.footerActions}>
            <button
              type="button"
              className={`${styles.button} ${styles.secondary}`}
              onClick={onBack}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.primary}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className={styles.loadingSpinner}></div>
                  Creating...
                </>
              ) : (
                <>
                  <FiSave size={16} />
                  Create Assignment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateAssignmentInline;
